import { Link } from 'react-router-dom';
import { GraduationCap, X } from 'lucide-react';
import { cn } from 'utils/cn';
import { ROUTES } from 'constants/routes';
import {
  useDismissEnrollmentNotice,
  useOpenEnrollmentNotices,
} from 'features/enrollment/hooks/useEnrollmentNotices';

function NoticeRow({
  message,
  batchId,
  onDismiss,
  dismissing,
}: {
  message: string;
  batchId: string;
  onDismiss: () => void;
  dismissing: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 border-b border-success-border/60 px-4 py-3 last:border-b-0',
        'bg-gradient-to-r from-success-muted via-surface-secondary to-success-muted',
      )}
      role="status"
    >
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-success/15 text-success">
        <GraduationCap className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-content">{message}</p>
        <Link
          to={ROUTES.BATCH_DETAIL.replace(':batchId', batchId)}
          className="mt-2 inline-flex text-sm font-medium text-success hover:underline"
        >
          Open batch
        </Link>
      </div>
      <button
        type="button"
        className="shrink-0 rounded-lg p-1.5 text-success hover:bg-success/10"
        aria-label="Dismiss enrollment notice"
        disabled={dismissing}
        onClick={onDismiss}
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

export function EnrollmentNoticeBanner() {
  const { data: notices = [], isLoading } = useOpenEnrollmentNotices();
  const dismiss = useDismissEnrollmentNotice();

  if (isLoading || notices.length === 0) return null;

  return (
    <div className="border-b border-success-border">
      {notices.map((notice) => (
        <NoticeRow
          key={notice.id}
          message={notice.message}
          batchId={notice.batchId}
          dismissing={dismiss.isPending && dismiss.variables === notice.id}
          onDismiss={() => dismiss.mutate(notice.id)}
        />
      ))}
    </div>
  );
}
